/**
 * The competitive half of Home: spec 6.1's Salesforce home page, kept whole and kept here, since
 * the floor runs on seeing everyone's week. The company against its goal, everyone's share,
 * the production trend, my long view.
 */
import { toUsDate, type AgentTotal } from "@tlcrm/shared";

import type { useHomeDashboard } from "../../api/hooks";
import { BarList, ChartCard, Donut, LineChart, Meter, Stat } from "../../components/charts";
import { PaceMark } from "../../components/kit";
import { paceOf } from "../../lib/cockpit";
import { compactMoney, dayLabel, percent } from "../../lib/chart";
import { money } from "../../lib/format";
import { Scorecard } from "../dashboards/Scorecard";

const usd = (n: number) => money(n);
const cents = (n: number) => money(n, true);

export function Competition({
  data,
  meId,
  elapsed,
}: {
  data: NonNullable<ReturnType<typeof useHomeDashboard>["data"]>;
  meId: string;
  /** How much of the sales week has gone, for the pace word on the goal. */
  elapsed: number;
}) {
  const { my, company, trends, overall } = data;
  const board = company.leaderboard;
  const top: AgentTotal | undefined = board[0];
  const pace = paceOf(company.premium, company.goal, elapsed);
  const mine = board.find((r) => r.owner.id === meId);
  const rows = board.map((r) => ({
    id: r.owner.id,
    label: r.owner.fullName,
    value: r.premium,
    emphasis: r.owner.id === meId,
    sub: `${r.count} ${r.count === 1 ? "policy" : "policies"}`,
  }));

  return (
    <div className="dash">
      <section className="dash-section">
        <h2>My weekly performance</h2>
        <div className="dash-grid">
          <div className="span-4">
            <Stat
              label="My total sales this week"
              value={money(my.premium)}
              sub={`${my.count} Closed Won`}
            />
          </div>
          <div className="span-8">
            <div className="stat">
              <span className="eyebrow">My policies Closed Won this week</span>
              <div style={{ marginTop: "var(--s-4)" }}>
                <Meter
                  value={my.count}
                  max={my.gaugeMax}
                  label="My policies Closed Won this week"
                  elapsed={elapsed}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="dash-section">
        <h2>
          The competition{" "}
          <small>
            Everyone's Closed Won premium this week
            {mine &&
              ` · your share ${company.premium ? percent(mine.premium / company.premium) : "–"}`}
          </small>
        </h2>
        <div className="stat-row">
          <Stat
            label="This week's sales"
            value={compactMoney(company.premium)}
            sub={
              <>
                <PaceMark pace={pace} />
                {company.goal
                  ? `${percent(company.premium / company.goal)} of goal`
                  : "No goal set"}
              </>
            }
          />
          <Stat
            label="This week's goal"
            value={compactMoney(company.goal)}
            sub={`${compactMoney(Math.max(0, company.goal - company.premium))} to go`}
          />
          <Stat
            label="Average deal size"
            value={money(company.averageDeal)}
            sub={`${company.count} policies sold`}
          />
          <Stat
            label="Most sold"
            value={
              <span className="stat-name truncate">
                {top ? (top.owner.id === meId ? "You" : top.owner.fullName) : "–"}
              </span>
            }
            sub={
              top
                ? `${money(top.premium)} · ${top.count} ${top.count === 1 ? "policy" : "policies"}`
                : "No sales yet this week"
            }
          />
        </div>
        <div className="dash-grid">
          <div className="span-7">
            <section className="card">
              <div className="card-head">
                <h3>Who is contributing the most?</h3>
              </div>
              <div className="card-body share">
                <Donut
                  slices={rows}
                  centre={compactMoney(company.premium)}
                  centreLabel="this week"
                />
                <table className="grid grid-compact">
                  <thead>
                    <tr>
                      <th>Owner</th>
                      <th className="num">Total Premium</th>
                      <th className="num">Policies</th>
                      <th className="num">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {board.map((r) => (
                      <tr key={r.owner.id} className={r.owner.id === meId ? "is-me" : undefined}>
                        <td className="primary">{r.owner.fullName}</td>
                        <td className="num mono">{cents(r.premium)}</td>
                        <td className="num mono">{r.count}</td>
                        <td className="num mono">
                          {company.premium ? percent(r.premium / company.premium) : "–"}
                        </td>
                      </tr>
                    ))}
                    {board.length === 0 && (
                      <tr>
                        <td colSpan={4} className="t3">
                          Nothing Closed Won yet this week.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {board.length > 0 && (
                    <tfoot>
                      <tr>
                        <td>Total</td>
                        <td className="num mono">{cents(company.premium)}</td>
                        <td className="num mono">{company.count}</td>
                        <td />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </section>
          </div>
          <div className="span-5">
            <ChartCard
              title="Current weekly sales"
              sub="You are in the accent"
              table={{
                columns: ["Owner", "Total Premium", "Policies"],
                rows: board.map((r) => [r.owner.fullName, cents(r.premium), r.count]),
              }}
            >
              {rows.length ? (
                <BarList rows={rows} format={compactMoney} />
              ) : (
                <p className="t3">No sales yet this week.</p>
              )}
            </ChartCard>
          </div>
        </div>
      </section>

      <section className="dash-section">
        <h2>Sales production trend</h2>
        <div className="dash-grid">
          <div className="span-6">
            <ChartCard
              title="Daily production"
              sub="Closed Won premium by the day it was entered, last 90 days"
              table={{
                columns: ["Day", "Premium"],
                rows: trends.daily.map((p) => [toUsDate(p.date), cents(p.value)]),
              }}
            >
              <LineChart
                points={trends.daily}
                format={usd}
                axisFormat={compactMoney}
                ariaLabel="Daily production, last 90 days"
              />
            </ChartCard>
          </div>
          <div className="span-6">
            <ChartCard
              title="Weekly production"
              sub="Closed Won premium by week of Date Sold, this year"
              table={{
                columns: ["Week of", "Premium"],
                rows: trends.weekly.map((p) => [toUsDate(p.date), cents(p.value)]),
              }}
            >
              <LineChart
                points={trends.weekly}
                format={usd}
                axisFormat={compactMoney}
                label={dayLabel}
                ariaLabel="Weekly production, this year"
              />
            </ChartCard>
          </div>
        </div>
      </section>

      <section className="dash-section">
        <h2>My overall performance</h2>
        <Scorecard overall={overall} full={false} />
      </section>
    </div>
  );
}
